import { useState, useEffect, useCallback, useRef } from 'react';
import { Room, RoomEvent, Track, DefaultReconnectPolicy } from 'livekit-client';
import { MatrixKeyProvider } from '../e2ee/MatrixKeyProvider';

const LIVEKIT_WS_URL = 'wss://r3ich.com/livekit';
const LIVEKIT_SERVICE_URL = 'https://r3ich.com/livekit/jwt';

export function useLiveKitRoom(matrixClient, matrixRoomId) {
  const [room, setRoom] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [participants, setParticipants] = useState([]);
  const [videoTracks, setVideoTracks] = useState(new Map());
  const [audioTracks, setAudioTracks] = useState(new Map());
  const [error, setError] = useState(null);
  const [e2eeEnabled, setE2eeEnabled] = useState(false);

  const roomRef = useRef(null);
  const rtcSessionRef = useRef(null);
  const keyProviderRef = useRef(null);

  // Connect to room
  const connect = useCallback(async () => {
    if (!matrixClient || !matrixRoomId) return;

    setError(null);
    setConnectionState('connecting');

    try {
      const matrixRoom = matrixClient.getRoom(matrixRoomId);
      if (!matrixRoom) {
        throw new Error('Room not found. Make sure you are a member of this room.');
      }

      // Check if room has encryption enabled
      const hasEncryption = matrixRoom.hasEncryptionStateEvent?.() || false;
      console.log(`[LiveKit] Room encryption state: ${hasEncryption}`);

      // Get MatrixRTC session for E2EE key management
      let rtcSession = null;
      let keyProvider = null;

      if (matrixClient.matrixRTC) {
        try {
          rtcSession = matrixClient.matrixRTC.getRoomSession(matrixRoom);
          rtcSessionRef.current = rtcSession;

          if (rtcSession) {
            console.log('[LiveKit] Got MatrixRTC session');
            console.log('[LiveKit] Session memberships:', rtcSession.memberships?.length || 0);

            // Create the LiveKit transport object for MatrixRTC
            const livekitTransport = {
              type: 'livekit',
              livekit_service_url: LIVEKIT_SERVICE_URL,
              livekit_alias: matrixRoomId,
            };

            // Join the MatrixRTC session to receive encryption keys
            console.log('[LiveKit] Joining MatrixRTC session...');
            console.log('[LiveKit] Current memberships:', rtcSession.memberships?.map(m => m.sender));

            rtcSession.joinRoomSession(
              [], // preferredFoci - empty for multi-SFU mode
              livekitTransport, // activeFocus
              {
                // manageMediaKeys: true is REQUIRED to receive encryption keys
                // The observer doesn't publish tracks, so it won't send its own keys
                // but it needs to receive keys from other participants to decrypt
                manageMediaKeys: true,
                useLegacyMemberEvents: false,
                useExperimentalToDeviceTransport: true,
                delayedLeaveEventRestartMs: 4000,
                delayedLeaveEventDelayMs: 18000,
              }
            );
            console.log('[LiveKit] Joined MatrixRTC session');

            // Listen for membership changes to trigger key re-requests
            rtcSession.on('memberships_changed', () => {
              console.log('[LiveKit] Memberships changed');
              console.log('[LiveKit] Current memberships:', rtcSession.memberships?.map(m => `${m.sender}:${m.deviceId}`));
              if (rtcSession.reemitEncryptionKeys) {
                console.log('[LiveKit] Requesting key reemit...');
                rtcSession.reemitEncryptionKeys();
              }
            });

            // Debug: Listen for raw to-device events
            matrixClient.on('toDeviceEvent', (event) => {
              console.log('[LiveKit] To-device event received:', event.getType(), event.getContent());
            });

            // Debug: Listen for encryption key changes directly on session
            rtcSession.on('encryption_key_changed', (key, index, participantId) => {
              console.log(`[LiveKit] DIRECT encryption_key_changed: participant=${participantId} index=${index} keyLength=${key?.length}`);
            });

            // Create key provider for E2EE
            keyProvider = new MatrixKeyProvider();
            keyProvider.setRTCSession(rtcSession);
            keyProviderRef.current = keyProvider;
            setE2eeEnabled(true);
          }
        } catch (e) {
          console.warn('[LiveKit] MatrixRTC not available:', e);
        }
      }

      // Get LiveKit token from lk-jwt-service using Matrix OpenID token
      // This ensures our LiveKit identity matches our Matrix identity
      console.log('[LiveKit] Getting OpenID token from Matrix client...');
      const openIdToken = await matrixClient.getOpenIdToken();
      console.log('[LiveKit] Got OpenID token, requesting LiveKit JWT from lk-jwt-service...');

      const tokenResponse = await fetch(`${LIVEKIT_SERVICE_URL}/sfu/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room: matrixRoomId,
          openid_token: openIdToken,
          device_id: matrixClient.getDeviceId(),
        }),
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        console.error('[LiveKit] Failed to get JWT from lk-jwt-service:', errorText);
        throw new Error(`Failed to get LiveKit token: ${errorText}`);
      }

      const sfuConfig = await tokenResponse.json();
      const token = sfuConfig.jwt;
      const ws_url = sfuConfig.url;
      console.log('[LiveKit] Got JWT from lk-jwt-service, connecting to:', ws_url);

      // Create LiveKit room with E2EE if key provider is available
      const roomOptions = {
        adaptiveStream: true,
        dynacast: true,
        reconnectPolicy: new DefaultReconnectPolicy(),
        disconnectOnPageLeave: false,
        preferredCodec: 'av1',
      };

      // Enable E2EE if we have a key provider
      if (keyProvider) {
        roomOptions.e2ee = {
          keyProvider: keyProvider,
          worker: new Worker(
            new URL('livekit-client/e2ee-worker', import.meta.url),
            { type: 'module' }
          ),
        };
        console.log('[LiveKit] E2EE enabled with MatrixRTC key provider');
      }

      const livekitRoom = new Room(roomOptions);
      roomRef.current = livekitRoom;

      // Set up event listeners
      livekitRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log(`[LiveKit] Track subscribed: ${track.kind} from ${participant.identity}`);

        if (track.kind === Track.Kind.Video) {
          setVideoTracks(prev => {
            const next = new Map(prev);
            next.set(`${participant.identity}-${track.sid}`, { track, publication, participant });
            return next;
          });
        } else if (track.kind === Track.Kind.Audio) {
          setAudioTracks(prev => {
            const next = new Map(prev);
            next.set(`${participant.identity}-${track.sid}`, { track, publication, participant });
            return next;
          });
        }
      });

      livekitRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        console.log(`[LiveKit] Track unsubscribed: ${track.kind} from ${participant.identity}`);

        if (track.kind === Track.Kind.Video) {
          setVideoTracks(prev => {
            const next = new Map(prev);
            next.delete(`${participant.identity}-${track.sid}`);
            return next;
          });
        } else if (track.kind === Track.Kind.Audio) {
          setAudioTracks(prev => {
            const next = new Map(prev);
            next.delete(`${participant.identity}-${track.sid}`);
            return next;
          });
        }
      });

      livekitRoom.on(RoomEvent.TrackPublished, (publication, participant) => {
        console.log(`[LiveKit] Track published: ${publication.kind} (${publication.source}) from ${participant.identity}`);
        if (!publication.isSubscribed) {
          console.log(`[LiveKit] Auto-subscribing to ${publication.trackSid}`);
          publication.setSubscribed(true);
        }
      });

      livekitRoom.on(RoomEvent.ParticipantConnected, (participant) => {
        console.log(`[LiveKit] Participant connected: ${participant.identity}`);
        setParticipants(prev => [...prev, participant]);
      });

      livekitRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
        console.log(`[LiveKit] Participant disconnected: ${participant.identity}`);
        setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
      });

      livekitRoom.on(RoomEvent.Disconnected, () => {
        console.log('[LiveKit] Disconnected');
        setConnectionState('disconnected');
      });

      livekitRoom.on(RoomEvent.Reconnecting, () => {
        console.log('[LiveKit] Reconnecting...');
        setConnectionState('reconnecting');
      });

      livekitRoom.on(RoomEvent.Reconnected, () => {
        console.log('[LiveKit] Reconnected');
        setConnectionState('connected');
      });

      // Connect to LiveKit
      console.log('[LiveKit] Connecting to', ws_url || LIVEKIT_WS_URL);
      await livekitRoom.connect(ws_url || LIVEKIT_WS_URL, token);

      setRoom(livekitRoom);
      setConnectionState('connected');

      // Initialize participants list
      const remoteParticipants = Array.from(livekitRoom.remoteParticipants.values());
      setParticipants(remoteParticipants);

      console.log('[LiveKit] Connected successfully');
      console.log(`[LiveKit] Found ${remoteParticipants.length} remote participants`);

      // Subscribe to all existing tracks from participants already in the room
      for (const participant of remoteParticipants) {
        console.log(`[LiveKit] Processing existing participant: ${participant.identity}`);
        for (const publication of participant.trackPublications.values()) {
          console.log(`[LiveKit] Found track: ${publication.kind} (${publication.source}) subscribed=${publication.isSubscribed}`);
          if (!publication.isSubscribed) {
            console.log(`[LiveKit] Subscribing to existing track ${publication.trackSid}`);
            publication.setSubscribed(true);
          }
          // If already subscribed, manually add to our state
          if (publication.track) {
            const track = publication.track;
            if (track.kind === Track.Kind.Video) {
              setVideoTracks(prev => {
                const next = new Map(prev);
                next.set(`${participant.identity}-${track.sid}`, { track, publication, participant });
                return next;
              });
            } else if (track.kind === Track.Kind.Audio) {
              setAudioTracks(prev => {
                const next = new Map(prev);
                next.set(`${participant.identity}-${track.sid}`, { track, publication, participant });
                return next;
              });
            }
          }
        }
      }

    } catch (e) {
      console.error('[LiveKit] Connection failed:', e);
      setError(e.message);
      setConnectionState('disconnected');
    }
  }, [matrixClient, matrixRoomId]);

  // Disconnect
  const disconnect = useCallback(async () => {
    // Leave MatrixRTC session
    if (rtcSessionRef.current) {
      try {
        console.log('[LiveKit] Leaving MatrixRTC session...');
        rtcSessionRef.current.leaveRoomSession();
      } catch (e) {
        console.warn('[LiveKit] Error leaving MatrixRTC session:', e);
      }
    }

    // Cleanup key provider
    if (keyProviderRef.current) {
      keyProviderRef.current.cleanup();
      keyProviderRef.current = null;
    }

    // Clear RTC session ref
    rtcSessionRef.current = null;

    if (roomRef.current) {
      await roomRef.current.disconnect();
      roomRef.current = null;
    }

    setRoom(null);
    setParticipants([]);
    setVideoTracks(new Map());
    setAudioTracks(new Map());
    setConnectionState('disconnected');
    setE2eeEnabled(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    room,
    connectionState,
    participants,
    videoTracks,
    audioTracks,
    error,
    e2eeEnabled,
    connect,
    disconnect,
  };
}
